import { createReducer, createAction, createSelector } from "@reduxjs/toolkit";
import { all, call, put, takeLatest } from "redux-saga/effects";

import { fetchConductivityVsSaltWeightFraction, fetchConductivityVsEisTemperature1000T } from "../lib/api";

export const actions = {
  request_samples_plot_data: createAction("REQUEST_SAMPLES_PLOT_DATA"),
  receive_samples_plot_data: createAction("RECEIVE_SAMPLES_PLOT_DATA"),
  receive_samples_plot_data_failed: createAction("RECEIVE_SAMPLES_PLOT_DATA_FAILED"),
  update_brush: createAction("UPDATE_PLOT_SELECTION"),
  clear_brush: createAction("clear_PLOT_SELECTION"),
};

export const constants = {
  LOADED: "LOADED",
  LOADING: "LOADING",
  ERROR: "ERROR",
  CONDUCTIVITY_VS_SALT_WEIGHT_FRACTION: "conductivityVsSaltWeightFraction",
  CONDUCTIVITY_VS_EIS_TEMPERATURE_1000T: "conductivityVsEisTemperature1000T",
};

const emptyPlotSelection = {
  plotKey: "",
  xKey: "",
  yKey: "",
  yRange: [0, 0],
  xRange: [0, 0],
};

export const initState = {
  loadingState: constants.LOADING,
  conductivityVsSaltWeightFraction: [],
  conductivityVsEisTemperature1000T: [],
  plotSelection: emptyPlotSelection,
};

export const actionMap = {
  [actions.request_samples_plot_data]: (state) => {
    state.loadingState = constants.LOADING;

    return state;
  },
  [actions.receive_samples_plot_data]: (state, action) => {
    state.conductivityVsSaltWeightFraction = action.payload.conductivityVsSaltWeightFraction;
    state.conductivityVsEisTemperature1000T = action.payload.conductivityVsEisTemperature1000T;
    state.plotSelection = emptyPlotSelection;
    state.loadingState = constants.LOADED;

    return state;
  },
  [actions.receive_samples_plot_data_failed]: (state) => {
    state.loadingState = constants.ERROR;

    return state;
  },
  [actions.update_brush]: (state, action) => {
    state.plotSelection.plotKey = action.payload.plotKey;
    state.plotSelection.xKey = action.payload.xKey;
    state.plotSelection.yKey = action.payload.yKey;
    state.plotSelection.yRange = action.payload.yRange;
    state.plotSelection.xRange = action.payload.xRange;

    return state;
  },
  [actions.clear_brush]: (state) => {
    state.plotSelection = emptyPlotSelection;

    return state;
  },
};

export const reducer = createReducer(initState, actionMap);

const getExperimentSamplesPlotData = (state) => state.experimentSamplesPlotData;
const empty = {};
const getIsSelectedById = createSelector([getExperimentSamplesPlotData], (samplesPlotData) => {
  const { plotKey, xKey, yKey, xRange, yRange } = samplesPlotData.plotSelection;
  if (!plotKey) {
    return empty;
  } else {
    return samplesPlotData[plotKey]
      .filter((d) => {
        return d[xKey] >= xRange[0] && d[xKey] <= xRange[1] && d[yKey] >= yRange[0] && d[yKey] <= yRange[1];
      })
      .reduce((acc, { sample_id }) => {
        acc[sample_id] = true;
        return acc;
      }, {});
  }
});

export const selectors = {
  getExperimentSamplesPlotData,
  getIsSelectedById,
};

function* onFetchSamplesPlotData(action) {
  try {
    const { accessToken } = action.payload;
    const [{ data: CVsSwf }, { data: CVsT1000t }] = yield all([
      call(fetchConductivityVsSaltWeightFraction, { accessToken }),
      call(fetchConductivityVsEisTemperature1000T, { accessToken }),
    ]);

    CVsT1000t.eis_temperature_t1000 = CVsT1000t.eis_temperature_c;

    if (
      CVsSwf.sample_id.length !== CVsSwf.conductivity_s_over_cm.length ||
      CVsSwf.sample_id.length !== CVsSwf.salt_weight_fraction.length ||
      CVsT1000t.sample_id.length !== CVsT1000t.conductivity_s_over_cm.length ||
      CVsT1000t.sample_id.length !== CVsT1000t.eis_temperature_t1000.length
    ) {
      throw Error(`
        Graph data was not tabular\n:
        Conductivity Vs Salt Weight Fraction Lengths:\n
        sample_id: ${CVsSwf.sample_id.length}\n 
        salt_weight_fraction: ${CVsSwf.salt_weight_fraction.length}\n
        conductivity_s_over_cm: ${CVsSwf.conductivity_s_over_cm.length}\n
        Conductivity Vs Temperature 1000 T:\n
        sample_id: ${CVsT1000t.sample_id.length}\n 
        eis_temperature_t1000: ${CVsT1000t.eis_temperature_t1000.length}\n
        conductivity_s_over_cm: ${CVsT1000t.conductivity_s_over_cm.length}\n
      `);
    }

    const conductivityVsSaltWeightFraction = [];
    for (let i = 0; i < CVsSwf.salt_weight_fraction.length; i++) {
      conductivityVsSaltWeightFraction.push({
        sample_id: CVsSwf.sample_id[i],
        salt_weight_fraction: CVsSwf.salt_weight_fraction[i],
        conductivity_s_over_cm: CVsSwf.conductivity_s_over_cm[i],
      });
    }

    const conductivityVsEisTemperature1000T = [];
    for (let i = 0; i < CVsT1000t.eis_temperature_t1000.length; i++) {
      conductivityVsEisTemperature1000T.push({
        sample_id: CVsT1000t.sample_id[i],
        conductivity_s_over_cm: CVsT1000t.conductivity_s_over_cm[i],
        eis_temperature_t1000: CVsT1000t.eis_temperature_t1000[i],
      });
    }

    yield put(
      actions.receive_samples_plot_data({
        conductivityVsSaltWeightFraction,
        conductivityVsEisTemperature1000T,
      })
    );
  } catch (e) {
    console.error(e);
    yield put(actions.receive_samples_plot_data_failed());
  }
}

export const sagas = {
  watchEveryRequestSamplesPlotData: function* watchEveryRequestSamplesPlotData() {
    yield takeLatest(actions.request_samples_plot_data.toString(), onFetchSamplesPlotData);
  },
};
